import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mail, User } from 'lucide-react';
import { publicProspectPageConfig } from '../../config/publicProspectPageConfig';
import {
  VALUATION_EXPENSE_KEYS,
  VALUATION_UNIT_MIX_KEYS,
  applyConstructionYearUnknown,
  deriveDeclaredUnitTotal,
} from './publicProspectValuation.js';
import {
  focusValuationStepHeading,
  prefersReducedMotion,
} from './publicProspectValuationFocus.js';
import PublicProspectMoneyInput from './PublicProspectMoneyInput.jsx';
import PublicProspectUnitCounter from './PublicProspectUnitCounter.jsx';

const UNIT_LABEL_KEYS = Object.freeze({
  oneAndHalf: 'unitOneAndHalf',
  twoAndHalf: 'unitTwoAndHalf',
  threeAndHalf: 'unitThreeAndHalf',
  fourAndHalf: 'unitFourAndHalf',
  fiveAndHalf: 'unitFiveAndHalf',
  sixAndHalfPlus: 'unitSixAndHalfPlus',
  commercial: 'unitCommercial',
  other: 'unitOther',
});

const EXPENSE_LABEL_KEYS = Object.freeze({
  municipalTaxes: 'municipalTaxes',
  schoolTaxes: 'schoolTaxes',
  insurance: 'insurance',
  heatingElectricity: 'heatingElectricity',
});

const STEP_HEADING_IDS = Object.freeze({
  1: 'valuation-financial-heading',
  2: 'valuation-building-heading',
  3: 'valuation-contact-heading',
});

export default function PublicProspectValuationForm({
  language,
  copy,
  form,
  valuation,
  valuationStep,
  errors,
  message,
  status,
  disabled,
  importedNumberOfUnits,
  importedConstructionYear = null,
  onValuationChange,
  onValuationPatch,
  onContactChange,
  onConsentChange,
  onSubmit,
  onBack,
  onNext,
  onRefresh,
  errorSummaryRef,
}) {
  const formCopy =
    publicProspectPageConfig.forms.property_value?.[language] ||
    publicProspectPageConfig.forms.property_value.fr;
  const err = (key) => (errors[key] ? copy.errors[errors[key]] || copy.errors.required : null);
  const declaredTotal = deriveDeclaredUnitTotal(valuation.unitMix) || 0;
  const hasImported =
    Number.isInteger(importedNumberOfUnits) && importedNumberOfUnits > 0;
  const showMismatch =
    hasImported && declaredTotal > 0 && declaredTotal !== importedNumberOfUnits;

  const mismatchText = formCopy.unitMismatch
    .replace('{declared}', String(declaredTotal))
    .replace('{imported}', String(importedNumberOfUnits));

  const stepHeadingRef = useRef(null);
  const pendingStepFocus = useRef(false);
  const unitMixErrorId = 'unitMix-error';
  const constructionYearErrorId = 'constructionYear-error';

  useEffect(() => {
    if (!pendingStepFocus.current) return;
    pendingStepFocus.current = false;
    focusValuationStepHeading(stepHeadingRef.current, {
      reducedMotion: prefersReducedMotion(),
    });
  }, [valuationStep]);

  const requestStepFocus = () => {
    pendingStepFocus.current = true;
  };

  const inputClass =
    'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 text-base text-[#1f4a52] focus:border-[#004747] focus:outline-none focus:ring-2 focus:ring-[#007c91] disabled:bg-gray-100 disabled:opacity-70';

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (valuationStep < 3) {
          requestStepFocus();
          onNext();
        } else onSubmit(event);
      }}
      className="space-y-5 motion-safe:transition-opacity"
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            requestStepFocus();
            onBack();
          }}
          disabled={disabled}
          className="rounded px-2 py-1 text-sm font-medium text-[#004747] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] disabled:opacity-60"
        >
          {copy.back}
        </button>
        <p className="text-sm font-medium text-[#1f4a52]">
          {formCopy.valuationProgress(valuationStep, 3)}
        </p>
      </div>

      <div>
        <h2 className="prospect-display text-[2rem] font-semibold leading-[1.08] tracking-tight text-[#0B1F2A]">
          {formCopy.heading}
        </h2>
        <p className="mt-2 text-[#1f4a52]">{formCopy.support}</p>
      </div>

      {message && status === 'retry' ? (
        <div
          ref={errorSummaryRef}
          tabIndex={-1}
          role="alert"
          className="rounded-lg border border-amber-500 bg-amber-50 p-3 text-amber-950"
        >
          {message}
        </div>
      ) : null}
      {Object.keys(errors).length ? (
        <div
          ref={errorSummaryRef}
          tabIndex={-1}
          role="alert"
          className="rounded-lg border border-red-500 bg-red-50 p-3 text-red-900"
        >
          {copy.errorSummary}
        </div>
      ) : null}

      {valuationStep === 1 ? (
        <section className="space-y-5" aria-labelledby={STEP_HEADING_IDS[1]}>
          <div>
            <h3
              id={STEP_HEADING_IDS[1]}
              ref={stepHeadingRef}
              tabIndex={-1}
              className="prospect-display text-[1.375rem] font-semibold leading-[1.1] text-[#0B1F2A] outline-none"
            >
              {formCopy.stepFinancialHeading}
            </h3>
            <p className="mt-1 text-sm text-[#1f4a52]">{formCopy.stepFinancialSupport}</p>
          </div>
          <PublicProspectMoneyInput
            id="gross-annual-income"
            label={formCopy.grossAnnualIncome}
            helpText={formCopy.grossAnnualIncomeHelp}
            language={language}
            placeholder={formCopy.moneyAmountPlaceholder}
            value={valuation.grossAnnualIncomeText}
            onChange={(value) => onValuationChange('grossAnnualIncomeText', value)}
            disabled={disabled}
            error={err('grossAnnualIncome')}
            errorId="grossAnnualIncome-error"
          />
          <div>
            <h4 className="font-semibold text-[#004747]">{formCopy.expensesHeading}</h4>
            <p className="mt-1 text-sm text-[#1f4a52]">{formCopy.expensesHelp}</p>
          </div>
          {VALUATION_EXPENSE_KEYS.map((key) => (
            <PublicProspectMoneyInput
              key={key}
              id={`expense-${key}`}
              label={formCopy[EXPENSE_LABEL_KEYS[key]]}
              helpText={formCopy.expenseBlankIsZeroHelp}
              language={language}
              placeholder={formCopy.moneyAmountPlaceholder}
              value={valuation[`${key}Text`]}
              onChange={(value) => onValuationChange(`${key}Text`, value)}
              disabled={disabled}
              error={err(key)}
              errorId={`${key}-error`}
              unknownChecked={valuation[`${key}Unknown`] === true}
              onUnknownChange={(checked) => onValuationChange(`${key}Unknown`, checked)}
              unknownLabel={formCopy.unknownExpense}
            />
          ))}
          <PublicProspectMoneyInput
            id="other-expenses"
            label={formCopy.otherExpenses}
            helpText={formCopy.expenseBlankIsZeroHelp}
            language={language}
            placeholder={formCopy.moneyAmountPlaceholder}
            value={valuation.otherExpensesText}
            onChange={(value) => onValuationChange('otherExpensesText', value)}
            disabled={disabled}
            error={err('otherExpenses')}
            errorId="otherExpenses-error"
          />
          <label className="block" htmlFor="other-expenses-description">
            <span className="font-medium text-[#004747]">{formCopy.otherExpensesDescription}</span>
            <span className="mt-1 block text-sm text-[#1f4a52]">
              {formCopy.otherExpensesDescriptionHelp}
            </span>
            <textarea
              id="other-expenses-description"
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base text-[#1f4a52] focus:border-[#004747] focus:outline-none focus:ring-2 focus:ring-[#007c91] disabled:bg-gray-100 disabled:opacity-70"
              rows={3}
              maxLength={500}
              value={valuation.otherExpensesDescription}
              disabled={disabled}
              aria-invalid={Boolean(errors.otherExpensesDescription)}
              aria-describedby={
                errors.otherExpensesDescription ? 'otherExpensesDescription-error' : undefined
              }
              onChange={(event) => onValuationChange('otherExpensesDescription', event.target.value)}
            />
            {err('otherExpensesDescription') ? (
              <p id="otherExpensesDescription-error" className="mt-1 text-sm font-medium text-red-700">
                {err('otherExpensesDescription')}
              </p>
            ) : null}
          </label>
        </section>
      ) : null}

      {valuationStep === 2 ? (
        <section className="space-y-5" aria-labelledby={STEP_HEADING_IDS[2]}>
          <div>
            <h3
              id={STEP_HEADING_IDS[2]}
              ref={stepHeadingRef}
              tabIndex={-1}
              className="prospect-display text-[1.375rem] font-semibold leading-[1.1] text-[#0B1F2A] outline-none"
            >
              {formCopy.stepBuildingHeading}
            </h3>
            <p className="mt-1 text-sm text-[#1f4a52]">{formCopy.stepBuildingSupport}</p>
          </div>
          <label className="block" htmlFor="construction-year">
            <span className="font-medium text-[#004747]">{formCopy.constructionYear}</span>
            <input
              id="construction-year"
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base text-[#1f4a52] focus:border-[#004747] focus:outline-none focus:ring-2 focus:ring-[#007c91] disabled:bg-gray-100 disabled:opacity-70"
              inputMode="numeric"
              autoComplete="off"
              value={valuation.constructionYearUnknown ? '' : valuation.constructionYearText}
              disabled={disabled || valuation.constructionYearUnknown}
              aria-invalid={Boolean(errors.constructionYear)}
              aria-describedby={
                [
                  Number.isInteger(importedConstructionYear) && importedConstructionYear > 0
                    ? 'construction-year-hint'
                    : null,
                  errors.constructionYear ? constructionYearErrorId : null,
                ]
                  .filter(Boolean)
                  .join(' ') || undefined
              }
              onChange={(event) => onValuationChange('constructionYearText', event.target.value)}
            />
            {Number.isInteger(importedConstructionYear) && importedConstructionYear > 0 ? (
              <p id="construction-year-hint" className="mt-1 text-sm text-[#1f4a52]">
                {formCopy.constructionYearHint}
              </p>
            ) : null}
          </label>
          <label
            className="inline-flex min-h-[2.75rem] items-center gap-3 text-sm leading-5 text-[#1f4a52]"
            htmlFor="construction-year-unknown"
          >
            <input
              id="construction-year-unknown"
              className="m-0 h-5 w-5 shrink-0"
              type="checkbox"
              checked={valuation.constructionYearUnknown === true}
              disabled={disabled}
              aria-invalid={Boolean(errors.constructionYear)}
              aria-describedby={errors.constructionYear ? constructionYearErrorId : undefined}
              onChange={(event) => {
                const next = applyConstructionYearUnknown(valuation, event.target.checked);
                if (typeof onValuationPatch === 'function') onValuationPatch(next);
                else {
                  onValuationChange('constructionYearUnknown', next.constructionYearUnknown);
                  onValuationChange('constructionYearText', next.constructionYearText);
                }
              }}
            />
            <span className="leading-5">{formCopy.constructionYearUnknown}</span>
          </label>
          {err('constructionYear') ? (
            <p id={constructionYearErrorId} className="text-sm font-medium text-red-700">
              {err('constructionYear')}
            </p>
          ) : null}

          <fieldset
            className="space-y-3"
            aria-describedby={errors.unitMix ? unitMixErrorId : undefined}
            aria-invalid={errors.unitMix ? 'true' : undefined}
          >
            <legend className="font-semibold text-[#004747]">{formCopy.unitMixHeading}</legend>
            {err('unitMix') ? (
              <p id={unitMixErrorId} className="text-sm font-medium text-red-700">
                {err('unitMix')}
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {VALUATION_UNIT_MIX_KEYS.map((key) => (
                <PublicProspectUnitCounter
                  key={key}
                  label={formCopy[UNIT_LABEL_KEYS[key]]}
                  value={valuation.unitMix[key]}
                  onChange={(next) =>
                    onValuationChange('unitMix', { ...valuation.unitMix, [key]: next })
                  }
                  disabled={disabled}
                  decrementLabel={formCopy.unitDecrement}
                  incrementLabel={formCopy.unitIncrement}
                  error={err(`unitMix.${key}`)}
                  errorId={`unitMix-${key}-error`}
                />
              ))}
            </div>
          </fieldset>

          {showMismatch ? (
            <div className="space-y-3 rounded-xl border border-amber-400 bg-amber-50 p-4 text-[#1f4a52]">
              <p>{mismatchText}</p>
              <label className="flex items-start gap-3" htmlFor="unit-mismatch-confirm">
                <input
                  id="unit-mismatch-confirm"
                  className="mt-1 min-h-[1.25rem] min-w-[1.25rem]"
                  type="checkbox"
                  checked={valuation.unitCountMismatchConfirmed === true}
                  disabled={disabled}
                  aria-invalid={Boolean(errors.unitCountMismatchConfirmed)}
                  aria-describedby={
                    errors.unitCountMismatchConfirmed ? 'unitCountMismatchConfirmed-error' : undefined
                  }
                  onChange={(event) =>
                    onValuationChange('unitCountMismatchConfirmed', event.target.checked)
                  }
                />
                <span>{formCopy.unitMismatchConfirm}</span>
              </label>
              {err('unitCountMismatchConfirmed') ? (
                <p id="unitCountMismatchConfirmed-error" className="text-sm font-medium text-red-700">
                  {err('unitCountMismatchConfirmed')}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {valuationStep === 3 ? (
        <section className="space-y-5" aria-labelledby={STEP_HEADING_IDS[3]}>
          <div>
            <h3
              id={STEP_HEADING_IDS[3]}
              ref={stepHeadingRef}
              tabIndex={-1}
              className="prospect-display text-[1.375rem] font-semibold leading-[1.1] text-[#0B1F2A] outline-none"
            >
              {formCopy.stepContactHeading}
            </h3>
            <p className="mt-1 text-sm text-[#1f4a52]">{formCopy.stepContactSupport}</p>
          </div>
          <label className="block">
            <span className="font-medium text-[#004747]">{copy.fullName}</span>
            <span className="relative mt-1 block">
              <User
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                className={inputClass}
                autoComplete="name"
                value={form.contact.fullName}
                disabled={disabled}
                aria-invalid={Boolean(errors.fullName)}
                aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                onChange={(event) => onContactChange('fullName', event.target.value)}
              />
            </span>
            {err('fullName') ? (
              <p id="fullName-error" className="mt-1 text-sm font-medium text-red-700">
                {err('fullName')}
              </p>
            ) : null}
          </label>
          <label className="block">
            <span className="font-medium text-[#004747]">{copy.email}</span>
            <span className="relative mt-1 block">
              <Mail
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                className={inputClass}
                type="email"
                autoComplete="email"
                value={form.contact.email}
                disabled={disabled}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'email-error' : undefined}
                onChange={(event) => onContactChange('email', event.target.value)}
              />
            </span>
            {err('email') ? (
              <p id="email-error" className="mt-1 text-sm font-medium text-red-700">
                {err('email')}
              </p>
            ) : null}
          </label>
          <label className="flex items-start gap-3 rounded-xl border border-gray-300 p-4" htmlFor="valuation-consent">
            <input
              id="valuation-consent"
              className="mt-1"
              type="checkbox"
              checked={form.consentAccepted === true}
              disabled={disabled}
              aria-invalid={Boolean(errors.consent)}
              aria-describedby={errors.consent ? 'consent-error' : undefined}
              onChange={(event) => onConsentChange(event.target.checked)}
            />
            <span>{copy.consent}</span>
          </label>
          {err('consent') ? (
            <p id="consent-error" className="text-sm font-medium text-red-700">
              {err('consent')}
            </p>
          ) : null}
          {disabled ? (
            <span className="inline-block text-gray-500">{copy.privacy}</span>
          ) : (
            <Link
              className="inline-block font-medium text-[#005f73] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              {copy.privacy}
            </Link>
          )}
        </section>
      ) : null}

      <div className="flex flex-col gap-3 pb-2 sm:flex-row">
        <button
          disabled={disabled}
          type="submit"
          className="prospect-ui inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-xl bg-[#004747] px-5 py-3 text-[1.0625rem] font-bold text-white hover:bg-[#003636] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] focus-visible:ring-offset-2 disabled:opacity-60"
        >
          {disabled
            ? copy.submitting
            : status === 'retry' && valuationStep === 3
              ? copy.retry
              : valuationStep < 3
                ? formCopy.next
                : formCopy.cta}
          <span aria-hidden="true">→</span>
        </button>
        {status === 'retry' && valuationStep === 3 ? (
          <button
            type="button"
            disabled={disabled}
            className="prospect-ui rounded-xl border border-[#004747] px-5 py-3 text-[1.0625rem] font-bold text-[#004747] disabled:opacity-60"
            onClick={onRefresh}
          >
            {copy.refresh}
          </button>
        ) : null}
      </div>
    </form>
  );
}
